import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage } from "../../lib/api";
import { qk } from "../../lib/query-keys";
import { Banner, Button, Sheet } from "../../ui";
import { getOutbox } from "./outbox";
import { useOnline, useOutboxItems } from "./useOutbox";

/** Shows this login's waiting jobs and lets them resolve the ones the server refused. */
export function OfflineBanner({ ownerId }: { ownerId: string }) {
  const items = useOutboxItems(ownerId);
  const online = useOnline();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const review = items.filter((i) => i.needsReview);
  const waiting = items.length - review.length;
  if (!items.length && online) return null;

  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(id);
    setError(null);
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: qk.jobsAll });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="offline-strip" style={{ padding: "8px 16px 0" }}>
      {!online && (
        <Banner tone="warn" live>
          No signal. {waiting ? `${waiting} job${waiting > 1 ? "s" : ""} waiting for signal.` : "You can still fill in a job; it sends when you're back online."}
        </Banner>
      )}
      {online && waiting > 0 && <Banner tone="info" live>Sending {waiting} saved job{waiting > 1 ? "s" : ""}…</Banner>}
      {review.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Banner tone="error">
            <span className="grow">{review.length} saved job{review.length > 1 ? "s need" : " needs"} your attention.</span>
            <Button small variant="secondary" onClick={() => setOpen(true)}>Review</Button>
          </Banner>
        </div>
      )}
      <Sheet open={open} title="Saved jobs" onClose={() => setOpen(false)}>
        <div className="stack">
          {error && <Banner tone="error">{error}</Banner>}
          {review.map((item) => (
            <div key={item.id} className="card stack">
              <strong className="mono">{item.payload.vehicle.vin}</strong>
              <span className="muted">{item.needsReview?.message}</span>
              {/* Enough to call the claim in by phone, so discarding never loses it. */}
              <span className="muted">
                {[item.payload.insurance?.customerName, item.payload.insurance?.customerPhone, item.payload.insurance?.carrier, item.payload.insurance?.policyNumber].filter(Boolean).join(" · ")}
                {` · saved ${new Date(item.createdAt).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`}
              </span>
              <div className="row">
                {item.needsReview?.code === "duplicate_vin" && (
                  <Button small busy={busy === item.id} onClick={() => act(item.id, () => getOutbox().sendAnyway(item.id))}>Send anyway</Button>
                )}
                <Button small variant="ghost" disabled={busy === item.id} onClick={() => window.confirm("Discard this claim? It is not on the portal, so make sure the office has it first.") && act(item.id, () => getOutbox().discard(item.id))}>Discard</Button>
              </div>
            </div>
          ))}
          {!review.length && <p className="muted">Nothing needs review.</p>}
        </div>
      </Sheet>
    </div>
  );
}
